import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/app/Header";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Radar, Target, Users, PlusCircle, X, Send, Trash2, ChevronDown, ChevronRight,
  ExternalLink, Loader2, Sparkles, ShieldCheck, Search,
} from "lucide-react";

const MODES = [
  { id: "trigger", label: "Who needs it now", Icon: Target, desc: "Companies with a recent public event that creates immediate need." },
  { id: "category", label: "Who fits", Icon: Users, desc: "Companies that match your ICP — broader, lower urgency." },
  { id: "shadow", label: "Competitor's customers", Icon: Radar, desc: "Companies visible in your competitors' public customer material." },
];

const BAND_STYLE = {
  hot:  "bg-amber-500/10 text-amber-300 border-amber-500/30",
  warm: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  low:  "bg-zinc-800/40 text-zinc-400 border-zinc-700",
};

function BandChip({ band, total }) {
  const style = BAND_STYLE[band] || BAND_STYLE.low;
  return (
    <span data-testid={`band-chip-${band}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${style}`}>
      {band} · {total}/14
    </span>
  );
}

function LeadCard({ lead, onPush, onDismiss }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissMode, setDismissMode] = useState(false);
  const isDup = lead.status === "duplicate";
  const dimmed = isDup ? "opacity-60" : "";
  const push = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${lead.id}/push`);
      if (r.data.duplicate_hit) toast.success("Contact already existed — added opportunity + note.");
      else toast.success("Pushed to Employz. Enrichment " + (r.data.enrichment_had_data ? "verified public contact." : "found nothing extra."));
      onPush(lead.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed.");
    } finally { setBusy(false); }
  };
  const dismiss = async (reason) => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/dismiss`, { reason });
      toast.info("Dismissed — future hunts will de-prioritise similar leads.");
      onDismiss(lead.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed.");
    } finally { setBusy(false); setDismissMode(false); }
  };
  const trigger = lead.trigger_found;
  const sources = lead.sources || [];
  return (
    <div data-testid={`lead-card-${lead.id}`} className={`rounded-2xl border border-zinc-800 bg-zinc-950/50 p-4 ${dimmed}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {lead.website ? (
              <a href={lead.website} target="_blank" rel="noreferrer" className="text-white font-display text-lg tracking-tight hover:text-emerald-300 truncate">
                {lead.company}
              </a>
            ) : (
              <span className="text-white font-display text-lg tracking-tight truncate">{lead.company}</span>
            )}
            {lead.band && <BandChip band={lead.band} total={lead.total} />}
            {isDup && <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 border border-zinc-800 px-2 py-0.5 rounded-full">already in your CRM</span>}
          </div>
          {lead.location && <div className="text-[11px] text-zinc-500 mb-1">{lead.location}</div>}
          {lead.what_they_do && <div className="text-sm text-zinc-300 mb-1.5">{lead.what_they_do}</div>}
          {trigger && (
            <div className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 mb-1.5">
              <Sparkles className="w-3 h-3" /> {trigger}
            </div>
          )}
          {lead.why_fit && <div className="text-[13px] text-emerald-300/90 mb-0.5">{lead.why_fit}</div>}
          {lead.outreach_angle && <div className="text-[12px] text-zinc-500 italic">Opener: {lead.outreach_angle}</div>}
          {lead.source_type === "competitor_shadow" && lead.query && (
            <div className="text-[11px] text-zinc-500 mt-1">Found in <span className="text-zinc-300">{lead.query}</span>&apos;s client list</div>
          )}
          {(lead.size_signal || lead.public_phone || lead.public_email) && (
            <div className="text-[11px] text-zinc-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
              {lead.size_signal && <span>{lead.size_signal}</span>}
              {lead.public_phone && <span>· {lead.public_phone}</span>}
              {lead.public_email && <span>· {lead.public_email}</span>}
            </div>
          )}
        </div>
      </div>

      {sources.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-900">
          <button data-testid={`lead-sources-${lead.id}`} onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-300">
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Sources · {sources.length}
          </button>
          {open && (
            <div className="mt-2 space-y-1">
              {sources.map((s, i) => (
                <div key={i} className="text-[11px] text-zinc-500 flex items-start gap-1.5">
                  <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="text-zinc-400">{s.claim}: </span>
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline break-all">{s.url}</a>
                  </div>
                </div>
              ))}
              {lead.scores && (
                <div className="text-[10px] text-zinc-600 mt-2 font-mono">
                  ICP {lead.scores.icp}/3 · Need {lead.scores.need}/3 · Intent {lead.scores.intent}/3 · Size {lead.scores.size}/2 · Location {lead.scores.location}/2 · Contact {lead.scores.contact}/1
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isDup && lead.status === "new" && (
        <div className="mt-3 pt-3 border-t border-zinc-900 flex items-center gap-2 flex-wrap">
          <button data-testid={`lead-push-${lead.id}`} onClick={push} disabled={busy} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-emerald-500/40 text-emerald-300 rounded hover:bg-emerald-500/10">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Push to Employz
          </button>
          {!dismissMode ? (
            <button data-testid={`lead-dismiss-${lead.id}`} onClick={() => setDismissMode(true)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-zinc-800 text-zinc-500 rounded hover:text-zinc-300">
              <Trash2 className="w-3 h-3" /> Dismiss
            </button>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {["wrong category", "too far", "too small", "bad data", "not interested"].map((r) => (
                <button key={r} data-testid={`dismiss-${r.replace(/\s/g, '-')}`} onClick={() => dismiss(r)} className="text-[10px] uppercase tracking-[0.16em] px-2 py-1 border border-zinc-800 text-zinc-400 rounded hover:border-rose-500/30 hover:text-rose-300">{r}</button>
              ))}
              <button onClick={() => setDismissMode(false)} className="p-1 text-zinc-600 hover:text-zinc-300"><X className="w-3 h-3" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LeadHunter() {
  const [mode, setMode] = useState("trigger");
  const [query, setQuery] = useState("");
  const [triggers, setTriggers] = useState([]);
  const [selectedTriggerIds, setSelectedTriggerIds] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [competitorDraft, setCompetitorDraft] = useState("");
  const [savedSearches, setSavedSearches] = useState([]);
  const [job, setJob] = useState(null);
  const [leads, setLeads] = useState([]);
  const [manageTriggers, setManageTriggers] = useState(false);
  const [newTriggerLabel, setNewTriggerLabel] = useState("");
  const [newTriggerIntent, setNewTriggerIntent] = useState("");
  const [generatingTriggers, setGeneratingTriggers] = useState(false);
  const [discovered, setDiscovered] = useState([]);
  const [discovering, setDiscovering] = useState(false);

  const loadDiscovered = () => {
    api.get("/leads/discovered-competitors").then((r) => setDiscovered(r.data?.rows || [])).catch(() => {});
  };

  const loadTriggers = () => {
    api.get("/leads/triggers").then((r) => {
      const rows = r.data?.rows || [];
      setTriggers(rows);
      setSelectedTriggerIds(rows.filter((t) => t.active).map((t) => t.id));
    }).catch(() => {});
  };

  const loadCompetitors = () => {
    api.get("/leads/competitors").then((r) => setCompetitors(r.data?.competitors || [])).catch(() => {});
  };

  const loadSaved = () => {
    api.get("/leads/saved-searches").then((r) => setSavedSearches(r.data?.rows || [])).catch(() => {});
  };

  const loadLeads = () => {
    api.get("/leads/hunted").then((r) => setLeads(r.data?.rows || [])).catch(() => {});
  };

  useEffect(() => {
    loadTriggers();
    loadCompetitors();
    loadSaved();
    loadLeads();
    loadDiscovered();
  }, []);

  // Poll active job every 1.5s until it terminates.
  useEffect(() => {
    if (!job || job.status === "done" || job.status === "failed") return;
    const t = setInterval(() => {
      api.get(`/leads/hunt/${job.id}`).then((r) => {
        setJob(r.data);
        if (r.data.status === "done" || r.data.status === "failed") { loadLeads(); loadDiscovered(); }
      }).catch(() => {});
    }, 1500);
    return () => clearInterval(t);
  }, [job?.id, job?.status]);

  // After a hunt finishes and we have new discoveries, keep polling
  // discoveries every 4s for up to 60s so the auto-discovery async task
  // eventually surfaces its results without needing a refresh.
  useEffect(() => {
    if (job?.status !== "done") return;
    let ticks = 0;
    const t = setInterval(() => {
      ticks += 1;
      loadDiscovered();
      if (ticks > 15) clearInterval(t);
    }, 4000);
    return () => clearInterval(t);
  }, [job?.status]);

  const findCompetitors = async () => {
    setDiscovering(true);
    try {
      await api.post("/leads/discovered-competitors/discover");
      toast.success("Competitor scan started — pulling from web search.");
      loadDiscovered();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed.");
    } finally { setDiscovering(false); }
  };

  const addDiscovered = async (d) => {
    try {
      await api.post(`/leads/discovered-competitors/${d.id}/add`);
      toast.success(`${d.name} added to your competitor list.`);
      setDiscovered((rows) => rows.map((r) => r.id === d.id ? { ...r, status: "added" } : r));
      loadCompetitors();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed."); }
  };

  const dismissDiscovered = async (d) => {
    try {
      await api.post(`/leads/discovered-competitors/${d.id}/dismiss`);
      setDiscovered((rows) => rows.map((r) => r.id === d.id ? { ...r, status: "dismissed" } : r));
    } catch (e) { toast.error(e?.response?.data?.detail || "Dismiss failed."); }
  };

  const startHunt = async () => {
    try {
      const payload = { mode, query };
      if (mode === "trigger") payload.trigger_ids = selectedTriggerIds;
      const r = await api.post("/leads/hunt", payload);
      setJob({ id: r.data.job_id, status: "queued", pass1_total: 0, pass2_done: 0 });
      toast.info("Hunt started — this takes 30-90 seconds.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start hunt.");
    }
  };

  const saveSearch = async () => {
    const label = window.prompt("Label this search:", `${mode} — ${new Date().toLocaleDateString()}`);
    if (!label) return;
    try {
      await api.post("/leads/saved-searches", { label, mode, query, trigger_ids: selectedTriggerIds });
      toast.success("Saved.");
      loadSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const generateTriggers = async () => {
    setGeneratingTriggers(true);
    try {
      const r = await api.post("/leads/triggers/generate");
      loadTriggers();
      toast.success(r.data?.generated ? `Generated ${r.data.rows.length} triggers from your profile.` : "Triggers already exist.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate triggers.");
    } finally { setGeneratingTriggers(false); }
  };

  const addTrigger = async () => {
    if (!newTriggerLabel.trim() || !newTriggerIntent.trim()) return;
    try {
      await api.post("/leads/triggers", { label: newTriggerLabel, search_intent: newTriggerIntent });
      setNewTriggerLabel(""); setNewTriggerIntent("");
      loadTriggers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed."); }
  };

  const toggleTrigger = async (t) => {
    try {
      await api.put(`/leads/triggers/${t.id}`, { label: t.label, search_intent: t.search_intent, active: !t.active });
      loadTriggers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed."); }
  };

  const deleteTrigger = async (t) => {
    if (!window.confirm(`Delete trigger "${t.label}"?`)) return;
    try {
      await api.delete(`/leads/triggers/${t.id}`);
      loadTriggers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const addCompetitor = async () => {
    const c = competitorDraft.trim();
    if (!c) return;
    const next = [...competitors, c];
    try {
      await api.put("/leads/competitors", { competitors: next });
      setCompetitors(next); setCompetitorDraft("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const removeCompetitor = async (c) => {
    const next = competitors.filter((x) => x !== c);
    try {
      await api.put("/leads/competitors", { competitors: next });
      setCompetitors(next);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const loadSaved_ = (s) => {
    setMode(s.mode);
    setQuery(s.query || "");
    if (s.trigger_ids?.length) setSelectedTriggerIds(s.trigger_ids);
  };

  const activeLeads = useMemo(() => leads.filter((l) => l.status === "new"), [leads]);
  const dupLeads = useMemo(() => leads.filter((l) => l.status === "duplicate"), [leads]);

  const jobStage = job?.status;
  const progressText = jobStage === "pass1" ? "Searching for signals…"
    : jobStage === "pass2" ? `Found ${job?.pass1_total || 0} candidates — researching each (${job?.pass2_done || 0}/${job?.pass1_total || 0})…`
    : jobStage === "pass3" ? "Scoring fit against your business…"
    : null;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-10 pt-8 pb-16">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400">// Lead Hunter</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tighter font-black text-white">
            Real companies. <span className="text-amber-400">Recent triggers.</span> Sourced links.
          </h1>
          <p className="mt-3 text-zinc-400 max-w-2xl text-sm md:text-base leading-relaxed">
            Every lead is grounded in a live web search — no guesses, no invented emails. Push the good ones straight into Employz.
          </p>
        </div>

        {/* Mode switcher */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {MODES.map((m) => (
            <button
              key={m.id}
              data-testid={`hunt-mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`text-left p-4 rounded-xl border transition-all ${mode === m.id ? "border-emerald-500/50 bg-emerald-950/30" : "border-zinc-800 bg-zinc-950/50 hover:border-zinc-700"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <m.Icon className={`w-4 h-4 ${mode === m.id ? "text-emerald-300" : "text-zinc-500"}`} />
                <div className={`text-sm font-display font-bold ${mode === m.id ? "text-white" : "text-zinc-300"}`}>{m.label}</div>
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">{m.desc}</div>
            </button>
          ))}
        </div>

        {/* Mode-specific inputs */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 mb-4">
          {mode === "trigger" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Active triggers</div>
                <button data-testid="manage-triggers" onClick={() => setManageTriggers(!manageTriggers)} className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 hover:text-emerald-300">
                  {manageTriggers ? "Done" : "Manage triggers"}
                </button>
              </div>
              {triggers.length === 0 ? (
                <div className="text-sm text-zinc-500 py-2">
                  No triggers yet.
                  <button data-testid="generate-triggers" onClick={generateTriggers} disabled={generatingTriggers} className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-emerald-500/40 text-emerald-300 rounded">
                    {generatingTriggers ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate from my profile
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {triggers.map((t) => {
                    const active = selectedTriggerIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        data-testid={`trigger-chip-${t.id}`}
                        onClick={() => setSelectedTriggerIds(active ? selectedTriggerIds.filter((x) => x !== t.id) : [...selectedTriggerIds, t.id])}
                        className={`text-[11px] px-2.5 py-1 rounded-full border ${active ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-zinc-900/50 text-zinc-400 border-zinc-800"}`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {manageTriggers && (
                <div className="mt-3 pt-3 border-t border-zinc-900 space-y-2">
                  {triggers.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={t.active} onChange={() => toggleTrigger(t)} className="accent-emerald-500" />
                      <span className="text-zinc-300 w-40 truncate">{t.label}</span>
                      <span className="text-zinc-500 flex-1 truncate">{t.search_intent}</span>
                      <button onClick={() => deleteTrigger(t)} className="text-zinc-600 hover:text-rose-400"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <input value={newTriggerLabel} onChange={(e) => setNewTriggerLabel(e.target.value)} placeholder="Label (e.g. New branch opened)" className="text-xs bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-zinc-300 w-56" />
                    <input value={newTriggerIntent} onChange={(e) => setNewTriggerIntent(e.target.value)} placeholder="Search intent phrase" className="text-xs bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-zinc-300 flex-1" />
                    <button onClick={addTrigger} className="text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-emerald-500/40 text-emerald-300 rounded">Add</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {mode === "category" && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">What kind of customer are you hunting?</div>
              <input
                data-testid="category-query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. mid-size Mumbai FMCG distributors switching to WhatsApp for reorders"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
          )}
          {mode === "shadow" && (
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">Competitors also chasing these leads</div>
                  <div className="text-[11px] text-zinc-600 max-w-xl">Real companies operating in your field, found via web search. Tap to select which to shadow-hunt.</div>
                </div>
                <button
                  data-testid="find-competitors"
                  onClick={findCompetitors}
                  disabled={discovering}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-amber-500/40 text-amber-300 rounded hover:bg-amber-500/10"
                >
                  {discovering ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Find competitors
                </button>
              </div>

              {/* Saved competitors — chips (already selected for shadow hunt) */}
              {competitors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {competitors.map((c) => (
                    <span key={c} data-testid={`competitor-${c}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                      {c}
                      <button onClick={() => removeCompetitor(c)} className="text-amber-400/60 hover:text-rose-400"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}

              {/* Discovered — cards you can add to your shadow list */}
              {discovered.filter((d) => d.status === "candidate").length === 0 && competitors.length === 0 ? (
                <div className="text-[12px] text-zinc-600 border border-dashed border-zinc-800 rounded-lg px-3 py-4">
                  None yet. They&apos;ll appear here automatically after your first hunt, or click <span className="text-amber-400">Find competitors</span> to run a scan now.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {discovered.filter((d) => d.status === "candidate").map((d) => (
                    <div key={d.id} data-testid={`discovered-${d.id}`} className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            {d.website ? (
                              <a href={d.website} target="_blank" rel="noreferrer" className="text-white font-display text-sm hover:text-amber-300 truncate">{d.name}</a>
                            ) : (
                              <span className="text-white font-display text-sm truncate">{d.name}</span>
                            )}
                          </div>
                          {d.one_line && <div className="text-[11px] text-zinc-400 mb-1">{d.one_line}</div>}
                          {d.size_signal && <div className="text-[10px] text-zinc-600">{d.size_signal}</div>}
                          {d.source_url && (
                            <a href={d.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-amber-400/80 hover:underline mt-1">
                              <ExternalLink className="w-2.5 h-2.5" /> source
                            </a>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            data-testid={`add-competitor-${d.id}`}
                            onClick={() => addDiscovered(d)}
                            className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 border border-amber-500/40 text-amber-300 rounded hover:bg-amber-500/10"
                          >
                            Add
                          </button>
                          <button
                            onClick={() => dismissDiscovered(d)}
                            className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 text-zinc-600 hover:text-zinc-400"
                            title="Dismiss"
                          >
                            <X className="w-2.5 h-2.5 inline" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Manual add — collapsed, secondary */}
              <div className="mt-3 pt-3 border-t border-zinc-900 flex gap-2">
                <input value={competitorDraft} onChange={(e) => setCompetitorDraft(e.target.value)} placeholder="Or add a competitor manually" className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-400" />
                <button data-testid="add-competitor" onClick={addCompetitor} className="text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-zinc-800 text-zinc-500 rounded hover:text-zinc-300"><PlusCircle className="w-3 h-3 inline mr-1" />Add</button>
              </div>
            </div>
          )}
        </div>

        {/* Hunt button + saved searches */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <button
            data-testid="start-hunt"
            onClick={startHunt}
            disabled={!!(job && job.status !== "done" && job.status !== "failed")}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 text-emerald-300 rounded hover:bg-emerald-500/10 disabled:opacity-50"
          >
            {job && job.status !== "done" && job.status !== "failed" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Hunt
          </button>
          <button data-testid="save-search" onClick={saveSearch} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-2 border border-zinc-800 text-zinc-400 rounded hover:text-zinc-200">
            Save this search
          </button>
          {savedSearches.map((s) => (
            <button key={s.id} onClick={() => loadSaved_(s)} className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-zinc-800 text-zinc-500 rounded hover:text-zinc-300">{s.label}</button>
          ))}
        </div>

        {/* Progress */}
        {progressText && (
          <div data-testid="hunt-progress" className="text-sm text-zinc-400 mt-4 mb-2 font-mono">{progressText}</div>
        )}
        {job?.status === "done" && job?.learning_decisions > 0 && (
          <div className="text-[11px] text-zinc-600 mb-3">Tuning to your choices — {job.learning_decisions} decisions learned.</div>
        )}

        {/* Sourced-links reminder */}
        <div className="mt-4 mb-4 text-[11px] text-zinc-500 border-l-2 border-emerald-500/40 pl-3 py-1 leading-relaxed flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400/70 mt-0.5 shrink-0" />
          <span>Every lead is sourced from public web pages — check the links before contacting anyone. We never generate personal names or private contact details.</span>
        </div>

        {/* Empty state */}
        {job?.status === "done" && activeLeads.length === 0 && dupLeads.length === 0 && (
          <div data-testid="hunt-empty" className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950/50 p-6 text-sm text-zinc-500">
            <div>{job.empty_reason || "Public data on this segment was limited — no verified companies found."}</div>
            {(job.dropped_irrelevant + job.dropped_no_sources + job.dropped_pass3_skip) > 0 && (
              <div className="mt-3 text-[11px] text-zinc-600 flex flex-wrap gap-x-4 gap-y-1 font-mono" data-testid="hunt-drop-counters">
                <span>Pass 1 candidates: <b className="text-zinc-400">{job.pass1_total}</b></span>
                {job.dropped_irrelevant > 0 && <span>Irrelevant: <b className="text-zinc-400">{job.dropped_irrelevant}</b></span>}
                {job.dropped_unverifiable > 0 && <span>Unverifiable: <b className="text-zinc-400">{job.dropped_unverifiable}</b></span>}
                {job.dropped_no_sources > 0 && <span>No sources: <b className="text-zinc-400">{job.dropped_no_sources}</b></span>}
                {job.dropped_pass3_skip > 0 && <span>Below fit threshold: <b className="text-zinc-400">{job.dropped_pass3_skip}</b></span>}
              </div>
            )}
          </div>
        )}

        {/* Result cards */}
        {activeLeads.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="lead-list">
            {activeLeads
              .sort((a, b) => {
                const bandOrder = { hot: 0, warm: 1, low: 2 };
                const ba = bandOrder[a.band] ?? 9, bb = bandOrder[b.band] ?? 9;
                if (ba !== bb) return ba - bb;
                return (b.total || 0) - (a.total || 0);
              })
              .map((l) => (
                <LeadCard
                  key={l.id}
                  lead={l}
                  onPush={(id) => setLeads((rows) => rows.map((r) => r.id === id ? { ...r, status: "pushed" } : r))}
                  onDismiss={(id) => setLeads((rows) => rows.map((r) => r.id === id ? { ...r, status: "dismissed" } : r))}
                />
              ))}
          </div>
        )}

        {dupLeads.length > 0 && (
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-2">Already in your CRM · {dupLeads.length}</div>
            <div className="space-y-2">
              {dupLeads.slice(0, 12).map((l) => (
                <div key={l.id} className="text-[12px] text-zinc-500 border border-zinc-900 bg-zinc-950/30 rounded-lg px-3 py-2">
                  {l.company} <span className="text-zinc-700">·</span> <span className="text-zinc-600">{l.what_they_do?.slice(0, 80) || l.website}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
